/**
 * Formatage PUR de l'ETA d'un job en un libellé compact, à partir d'un gabarit
 * i18n `{value}`. Le CALCUL (secondes → « 2 min » / « 30 s ») est isolé ici et
 * testé sans dépendance ; l'INTERPOLATION du gabarit reste au composant (via le
 * helper `interpolate` partagé), pour garder les libellés hors de ce module.
 *
 * Deux gabarits sont fournis par l'appelant :
 * - `secondsTemplate` (ex. « ≈ {value} s ») pour les durées < 60 s ;
 * - `minutesTemplate` (ex. « ≈ {value} min ») pour les durées ≥ 60 s (arrondi à
 *   la minute supérieure, minimum 1).
 *
 * `null`/négatif ⇒ pas d'ETA affichable (`null`).
 */

export interface EtaTemplates {
  /** Gabarit pour une durée exprimée en secondes (`{value}` = nombre de secondes). */
  secondsTemplate: string;
  /** Gabarit pour une durée exprimée en minutes (`{value}` = nombre de minutes). */
  minutesTemplate: string;
}

/** Unité choisie et valeur entière associée, pour interpolation par l'appelant. */
export interface EtaParts {
  unit: 'seconds' | 'minutes';
  value: number;
  template: string;
}

/** Seuil de bascule seconde → minute (60 s). */
const MINUTE_SECONDS = 60;

/**
 * Décide de l'unité et de la valeur à afficher pour un ETA en secondes.
 * `null` si l'ETA est absent ou négatif (rien à afficher).
 */
export function etaParts(etaSeconds: number | null, templates: EtaTemplates): EtaParts | null {
  if (etaSeconds === null || !Number.isFinite(etaSeconds) || etaSeconds < 0) return null;
  const secs = Math.round(etaSeconds);
  if (secs < MINUTE_SECONDS) {
    return { unit: 'seconds', value: secs, template: templates.secondsTemplate };
  }
  return {
    unit: 'minutes',
    value: Math.max(1, Math.ceil(secs / MINUTE_SECONDS)),
    template: templates.minutesTemplate,
  };
}
